import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, RefreshCw, Wifi, Copy } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Badge from '@/components/ui/Badge';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface ChannelForm {
  name: string;
  platform: string;
  status: string;
  pricing_rule_type: string;
  pricing_rule_value: string;
  sync_enabled: boolean;
  sync_interval_minutes: string;
  settings: Record<string, string>;
}

const PLATFORMS = [
  { label: 'Google Shopping', value: 'google_shopping' },
  { label: 'Facebook / Instagram Shopping', value: 'facebook_shopping' },
  { label: 'Amazon', value: 'amazon' },
  { label: 'eBay', value: 'ebay' },
  { label: 'Etsy', value: 'etsy' },
  { label: 'Walmart', value: 'walmart' },
  { label: 'TikTok Shop', value: 'tiktok_shop' },
];

const STATUSES = [
  { label: 'Inactive', value: 'inactive' },
  { label: 'Active', value: 'active' },
  { label: 'Paused', value: 'paused' },
];

const PRICING_RULES = [
  { label: 'No adjustment', value: 'none' },
  { label: 'Markup (percentage)', value: 'markup_percent' },
  { label: 'Markup (fixed amount)', value: 'markup_fixed' },
  { label: 'Markdown (percentage)', value: 'markdown_percent' },
  { label: 'Markdown (fixed amount)', value: 'markdown_fixed' },
];

const SYNC_INTERVALS = [
  { label: '15 minutes', value: '15' },
  { label: '30 minutes', value: '30' },
  { label: '1 hour', value: '60' },
  { label: '2 hours', value: '120' },
  { label: '6 hours', value: '360' },
  { label: '12 hours', value: '720' },
  { label: '24 hours', value: '1440' },
];

const INITIAL_FORM: ChannelForm = {
  name: '',
  platform: 'google_shopping',
  status: 'inactive',
  pricing_rule_type: 'none',
  pricing_rule_value: '0',
  sync_enabled: false,
  sync_interval_minutes: '60',
  settings: {},
};

const FEED_PLATFORMS = ['google_shopping', 'facebook_shopping', 'tiktok_shop'];
// Amazon supports both feed and API mode — feed URL shown when integration_mode is 'feed'
const API_URL = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const MarketplaceChannelEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = id && id !== 'new';
  const [formData, setFormData] = useState<ChannelForm>(INITIAL_FORM);
  const [isLoading, setIsLoading] = useState(!!isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncLogs, setSyncLogs] = useState<any[]>([]);
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchChannel = async () => {
      try {
        setIsLoading(true);
        const { channel, sync_logs } = await api.get<{ channel: any; sync_logs: any[] }>(`/admin/marketplace-channels/${id}`);
        setFormData({
          name: channel.name,
          platform: channel.platform,
          status: channel.status,
          pricing_rule_type: channel.pricing_rule_type || 'none',
          pricing_rule_value: String(channel.pricing_rule_value || 0),
          sync_enabled: channel.sync_enabled,
          sync_interval_minutes: String(channel.sync_interval_minutes || 60),
          settings: (typeof channel.settings === 'object' && channel.settings) || {},
        });
        setSyncLogs(sync_logs || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load channel');
      } finally {
        setIsLoading(false);
      }
    };
    fetchChannel();
  }, [id, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        name: formData.name,
        platform: formData.platform,
        status: formData.status,
        pricing_rule_type: formData.pricing_rule_type,
        pricing_rule_value: parseFloat(formData.pricing_rule_value) || 0,
        sync_enabled: formData.sync_enabled,
        sync_interval_minutes: parseInt(formData.sync_interval_minutes, 10) || 60,
        settings: formData.settings,
      };

      if (isEditMode) {
        await api.put(`/admin/marketplace-channels/${id}`, payload);
        addToast('Channel updated', 'success');
      } else {
        const { channel } = await api.post<{ channel: any }>('/admin/marketplace-channels', payload);
        addToast('Channel created', 'success');
        navigate(`/admin/marketplace/channels/${channel.id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save channel');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!isEditMode) return;
    setIsTesting(true);
    try {
      const result = await api.post<{ ok: boolean; error?: string }>(`/admin/marketplace-channels/${id}/test-connection`, {});
      if (result.ok) {
        addToast('Connection successful', 'success');
      } else {
        addToast(`Connection failed: ${result.error}`, 'error');
      }
    } catch (err: any) {
      addToast(`Test failed: ${err.message}`, 'error');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSync = async () => {
    if (!isEditMode) return;
    setIsSyncing(true);
    try {
      await api.post(`/admin/marketplace-channels/${id}/sync`, {});
      addToast('Sync completed', 'success');
    } catch (err: any) {
      addToast(`Sync failed: ${err.message}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleChange = (field: keyof ChannelForm, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSettingChange = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value },
    }));
  };

  const copyFeedUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    addToast('Feed URL copied to clipboard', 'success');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const isAmazonFeedMode = formData.platform === 'amazon' && (formData.settings.integration_mode || 'feed') === 'feed';
  const isFeedPlatform = FEED_PLATFORMS.includes(formData.platform) || isAmazonFeedMode;
  const feedUrl = isEditMode && isFeedPlatform
    ? formData.platform === 'google_shopping'
      ? `${API_URL}/feeds/google-shopping/${id}.xml`
      : formData.platform === 'tiktok_shop'
        ? `${API_URL}/feeds/tiktok/${id}.xml`
        : formData.platform === 'amazon'
          ? `${API_URL}/feeds/amazon/${id}.${formData.settings.feed_format === 'tsv' ? 'tsv' : 'xml'}`
          : `${API_URL}/feeds/facebook/${id}.xml`
    : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/marketplace/channels" className="p-2 hover:bg-stone-100 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-stone-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? formData.name || 'Edit Channel' : 'New Channel'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Configure channel settings and integrations' : 'Add a new sales channel or shopping feed'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card title="Channel Information" padding="md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Channel Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder="e.g., Google Shopping - US"
                />
                <Select
                  label="Platform"
                  value={formData.platform}
                  onChange={(e) => handleChange('platform', e.target.value)}
                  options={PLATFORMS}
                  disabled={!!isEditMode}
                />
                <Select
                  label="Status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  options={STATUSES}
                />
              </div>
            </Card>

            <Card title="Pricing Rules" padding="md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Price Adjustment"
                  value={formData.pricing_rule_type}
                  onChange={(e) => handleChange('pricing_rule_type', e.target.value)}
                  options={PRICING_RULES}
                />
                {formData.pricing_rule_type !== 'none' && (
                  <Input
                    label={formData.pricing_rule_type.includes('percent') ? 'Percentage (%)' : 'Amount ($)'}
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing_rule_value}
                    onChange={(e) => handleChange('pricing_rule_value', e.target.value)}
                  />
                )}
              </div>
              {formData.pricing_rule_type !== 'none' && (
                <p className="text-sm text-gray-500 mt-2">
                  Example: A $10.00 product will be listed at $
                  {(() => {
                    const base = 10;
                    const val = parseFloat(formData.pricing_rule_value) || 0;
                    switch (formData.pricing_rule_type) {
                      case 'markup_percent': return (base * (1 + val / 100)).toFixed(2);
                      case 'markup_fixed': return (base + val).toFixed(2);
                      case 'markdown_percent': return (base * (1 - val / 100)).toFixed(2);
                      case 'markdown_fixed': return Math.max(0, base - val).toFixed(2);
                      default: return base.toFixed(2);
                    }
                  })()}
                </p>
              )}
            </Card>

            {/* Platform-specific settings */}
            <Card title="Platform Settings" padding="md">
              {formData.platform === 'google_shopping' && (
                <div className="space-y-4">
                  <Input
                    label="Google Merchant Center ID"
                    value={formData.settings.merchant_id || ''}
                    onChange={(e) => handleSettingChange('merchant_id', e.target.value)}
                    placeholder="123456789"
                    helperText="Your Google Merchant Center account ID"
                  />
                  {feedUrl && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Feed URL</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={feedUrl}
                          className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-600"
                        />
                        <button type="button" onClick={() => copyFeedUrl(feedUrl)} className="p-2 hover:bg-gray-100 rounded-md">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Add this URL in your Google Merchant Center as a scheduled fetch</p>
                    </div>
                  )}
                </div>
              )}

              {formData.platform === 'facebook_shopping' && (
                <div className="space-y-4">
                  <Input
                    label="Facebook Catalog ID"
                    value={formData.settings.catalog_id || ''}
                    onChange={(e) => handleSettingChange('catalog_id', e.target.value)}
                    placeholder="123456789"
                    helperText="Your Meta Commerce Manager catalog ID"
                  />
                  {feedUrl && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Feed URL</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={feedUrl}
                          className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-600"
                        />
                        <button type="button" onClick={() => copyFeedUrl(feedUrl)} className="p-2 hover:bg-gray-100 rounded-md">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Add this URL in your Meta Commerce Manager as a data feed source</p>
                    </div>
                  )}
                </div>
              )}

              {formData.platform === 'tiktok_shop' && (
                <div className="space-y-4">
                  <Input
                    label="TikTok Shop ID"
                    value={formData.settings.shop_id || ''}
                    onChange={(e) => handleSettingChange('shop_id', e.target.value)}
                    placeholder="Your TikTok Shop ID"
                    helperText="Found in your TikTok Seller Center settings"
                  />
                  {feedUrl && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Feed URL</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={feedUrl}
                          className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-600"
                        />
                        <button type="button" onClick={() => copyFeedUrl(feedUrl)} className="p-2 hover:bg-gray-100 rounded-md">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Add this URL in your TikTok Seller Center as a product catalog feed</p>
                    </div>
                  )}
                </div>
              )}

              {formData.platform === 'amazon' && (
                <div className="space-y-4">
                  <Select
                    label="Integration Mode"
                    value={formData.settings.integration_mode || 'feed'}
                    onChange={(e) => handleSettingChange('integration_mode', e.target.value)}
                    options={[
                      { label: 'Feed (bulk upload to Seller Central)', value: 'feed' },
                      { label: 'SP-API (live sync)', value: 'api' },
                    ]}
                  />
                  <Input
                    label="Amazon Seller ID"
                    value={formData.settings.seller_id || ''}
                    onChange={(e) => handleSettingChange('seller_id', e.target.value)}
                    placeholder="A1B2C3D4E5F6G7"
                    helperText="Your Amazon Seller Central merchant ID"
                  />
                  {(formData.settings.integration_mode || 'feed') === 'feed' && (
                    <>
                      <Select
                        label="Feed Format"
                        value={formData.settings.feed_format || 'xml'}
                        onChange={(e) => handleSettingChange('feed_format', e.target.value)}
                        options={[
                          { label: 'XML (Amazon Envelope)', value: 'xml' },
                          { label: 'TSV (Flat File / Tab-delimited)', value: 'tsv' },
                        ]}
                      />
                      {feedUrl && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Feed URL</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={feedUrl}
                              className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-600"
                            />
                            <button type="button" onClick={() => copyFeedUrl(feedUrl)} className="p-2 hover:bg-gray-100 rounded-md">
                              <Copy className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Download this feed and upload to Seller Central, or use for automated feed submission</p>
                        </div>
                      )}
                    </>
                  )}
                  {formData.settings.integration_mode === 'api' && (
                    <>
                      <Input
                        label="Marketplace ID"
                        value={formData.settings.marketplace_id || 'ATVPDKIKX0DER'}
                        onChange={(e) => handleSettingChange('marketplace_id', e.target.value)}
                        placeholder="ATVPDKIKX0DER"
                        helperText="US: ATVPDKIKX0DER, UK: A1F83G8C2ARO7P, DE: A1PA6795UKMFR9"
                      />
                      <Select
                        label="SP-API Region"
                        value={formData.settings.sp_api_region || 'na'}
                        onChange={(e) => handleSettingChange('sp_api_region', e.target.value)}
                        options={[
                          { label: 'North America', value: 'na' },
                          { label: 'Europe', value: 'eu' },
                          { label: 'Far East', value: 'fe' },
                        ]}
                      />
                      <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        SP-API credentials (Client ID, Client Secret, Refresh Token) are stored securely via the credentials field.
                        Use the Test Connection button to verify your API access.
                      </p>
                    </>
                  )}
                </div>
              )}

              {!isFeedPlatform && formData.platform !== 'amazon' && (
                <div className="text-center py-6">
                  <p className="text-gray-500">OAuth integration for {PLATFORMS.find((p) => p.value === formData.platform)?.label} is coming in a future update.</p>
                </div>
              )}
            </Card>

            {/* Sync Logs (edit mode) */}
            {isEditMode && syncLogs.length > 0 && (
              <Card title="Recent Sync Activity" padding="md">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-stone-500 uppercase">Items</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-stone-500 uppercase">Duration</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-stone-500 uppercase">Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {syncLogs.slice(0, 10).map((log: any) => (
                        <tr key={log.id} className="hover:bg-stone-50">
                          <td className="px-3 py-2 text-sm">{log.sync_type}</td>
                          <td className="px-3 py-2">
                            <Badge variant={log.status === 'success' ? 'green' : log.status === 'error' ? 'red' : 'yellow'}>
                              {log.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-sm text-right">
                            {log.items_succeeded}/{log.items_processed}
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-gray-500">
                            {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-500">
                            {new Date(log.started_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card title="Sync Settings" padding="md">
              <div className="space-y-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.sync_enabled}
                    onChange={(e) => handleChange('sync_enabled', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable automatic sync</span>
                </label>
                {formData.sync_enabled && (
                  <Select
                    label="Sync Interval"
                    value={formData.sync_interval_minutes}
                    onChange={(e) => handleChange('sync_interval_minutes', e.target.value)}
                    options={SYNC_INTERVALS}
                  />
                )}
              </div>
            </Card>

            {isEditMode && (
              <Card title="Actions" padding="md">
                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleTestConnection}
                    isLoading={isTesting}
                  >
                    <Wifi className="w-4 h-4 mr-2" />
                    Test Connection
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleSync}
                    isLoading={isSyncing}
                    disabled={formData.status !== 'active'}
                    title={formData.status !== 'active' ? 'Channel must be active to sync' : undefined}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync Now
                  </Button>
                  <Link
                    to={`/admin/marketplace/listings?channel_id=${id}`}
                    className="block w-full text-center text-sm text-blue-600 hover:text-blue-800 py-2"
                  >
                    View Channel Listings
                  </Link>
                </div>
              </Card>
            )}

            <Button variant="primary" size="md" type="submit" isLoading={isSaving} className="w-full">
              <Save className="w-5 h-5 mr-2" />
              {isEditMode ? 'Save Changes' : 'Create Channel'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MarketplaceChannelEditor;
